import { Bars3Icon } from "@heroicons/react/24/outline";
import HomeLink from "components/HomeLink";
import StarButton from "octo-buttons/lib/components/StarButton";
import { Link, navigate } from "gatsby";
import React, { PropsWithChildren, useEffect, useState } from "react";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import styled from "styled-components";
import { DownloadURL } from "constants/download";
import { OS, getDeviceOS } from "utils/detect-os";
import Banner from "./Banner";
import { useLocation } from "@reach/router";
import { Container } from "react-bootstrap";
import { URLwithUTMSuffix } from "utils/download";

export interface NavbarProps {
    /**
     * The path for this current page
     *
     * @see LayoutProps.path
     */
    path?: string;
    isPageAuth?: boolean;
    shouldShowBanner?: boolean;
}

const CustomNavbar: React.FC<NavbarProps> = ({
    path,
    isPageAuth,
    shouldShowBanner = false,
}) => {
    // Normalize the path for comparison by removing any trailing slash
    const currentPath = path?.replace(/\/$/, "");

    const [expanded, setExpanded] = useState(false);

    interface NavLinkProps {
        /* The path to the current page, without any trailing slashes */
        currentPath?: string;
        to: string;
    }

    const CustomNavLink: React.FC<PropsWithChildren<NavLinkProps>> = ({
        currentPath,
        to,
        children,
    }) => {
        const isCurrentPage = currentPath?.startsWith(to);

        // Check if the link is external by looking for http/https or different domain
        const isExternal =
            /^(https?:)?\/\//.test(to) ||
            (to.startsWith("//") && !to.startsWith("//localhost"));

        const handleLinkClick = (
            event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        ) => {
            // Handle internal navigation
            if (!isExternal) {
                if (
                    (currentPath === "/auth" || currentPath === "/") &&
                    (to === "/#pricing" || to === "/#features")
                ) {
                    event.preventDefault();
                    const redirectSection = document.getElementById(
                        to.substring(2),
                    );
                    if (redirectSection) {
                        redirectSection.scrollIntoView({
                            behavior: "smooth",
                            block: "end",
                        });
                    }
                } else {
                    navigate(to);
                }
                setTimeout(() => {
                    setExpanded(false);
                }, 150);
            }
        };

        // For external links, use a regular anchor tag with target="_blank"
        if (isExternal) {
            return (
                <a
                    href={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`nav-link ${isCurrentPage ? "navbar-link navbar-link-active" : "navbar-link"}`}
                >
                    {children}
                </a>
            );
        }

        // For internal links, use the existing Link component
        return isCurrentPage ? (
            <Link
                onClick={handleLinkClick}
                to={to}
                aria-current="page"
                className="nav-link navbar-link navbar-link-active"
            >
                {children}
            </Link>
        ) : (
            <Link
                onClick={handleLinkClick}
                to={to}
                className="nav-link navbar-link"
            >
                {children}
            </Link>
        );
    };

    const toggleHandler = (expanded: boolean) => {
        const navbar =
            document.querySelector<HTMLDivElement>(".navbar-collapse");
        if (navbar) {
            navbar.style.transition =
                "min-height .25s ease-out, height .25s ease-out";
            navbar.style.minHeight = "0vh";
            if (expanded) {
                navbar.style.borderTop = "1px solid #ccc";
                navbar.style.marginTop = "10px";
                navbar.style.paddingTop = "10px";
                navbar.style.minHeight = "100vh";
            } else {
                navbar.style.borderTop = "none";
                navbar.style.marginTop = "0px";
                navbar.style.paddingTop = "0px";
                navbar.style.minHeight = "0vh";
            }
        }
    };
    const btnAccentColor = isPageAuth ? "#6424bd" : "#08c225";
    const btnBackgroundColor = isPageAuth
        ? "#6424bd"
        : "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABaWlDQ1BEaXNwbGF5IFAzAAB4nHWQvUvDUBTFT6tS0DqIDh0cMolD1NIKdnFoKxRFMFQFq1OafgltfCQpUnETVyn4H1jBWXCwiFRwcXAQRAcR3Zw6KbhoeN6XVNoi3sfl/Ticc7lcwBtQGSv2AijplpFMxKS11Lrke4OHnlOqZrKooiwK/v276/PR9d5PiFlNu3YQ2U9cl84ul3aeAlN//V3Vn8maGv3f1EGNGRbgkYmVbYsJ3iUeMWgp4qrgvMvHgtMunzuelWSc+JZY0gpqhrhJLKc79HwHl4plrbWD2N6f1VeXxRzqUcxhEyYYilBRgQQF4X/8044/ji1yV2BQLo8CLMpESRETssTz0KFhEjJxCEHqkLhz634PrfvJbW3vFZhtcM4v2tpCAzidoZPV29p4BBgaAG7qTDVUR+qh9uZywPsJMJgChu8os2HmwiF3e38M6Hvh/GMM8B0CdpXzryPO7RqFn4Er/QcXKWq8MSlPPgAAABBJREFUeAEBBQD6/wAIwiX/A7QB7/maltEAAAAASUVORK5CYII=) repeat";
    const btnBorder = isPageAuth
        ? "rgb(100, 36, 189, 0.1)"
        : "rgb(1, 183, 47, 0.1)";
    const btnOutline = isPageAuth
        ? "rgb(100, 36, 189, 0.5)"
        : "rgb(1, 183, 47, 0.5)";
    const loginLink = isPageAuth
        ? "https://auth.ente.io"
        : "https://web.ente.io";

    const authPlayURL = URLwithUTMSuffix(DownloadURL.authGooglePlay);
    const authAppStoreURL = URLwithUTMSuffix(DownloadURL.authAppStore);
    const photosPlayURL = URLwithUTMSuffix(DownloadURL.googlePlay);
    const photosAppStoreURL = URLwithUTMSuffix(DownloadURL.appStore);

    const handleClick = () => {
        const os = getDeviceOS();

        const androidDownloadLink = isPageAuth ? authPlayURL : photosPlayURL;
        const iosDownloadLink = isPageAuth
            ? authAppStoreURL
            : photosAppStoreURL;
        const downloadSectionID = isPageAuth ? "download-auth" : "download";
        if (os === OS.Android) {
            window.location.href = androidDownloadLink;
        } else if (os === OS.IOS) {
            window.location.href = iosDownloadLink;
        } else {
            const downloadSection = document.getElementById(downloadSectionID);
            if (downloadSection) {
                downloadSection.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                });
            }
        }
    };

    const location = useLocation();
    const [pathname, setPathname] = useState("");

    useEffect(() => {
        setPathname(location.pathname);
    }, [location.pathname]);

    let pageIDForCTA = "";

    if (pathname === "/take-control" || pathname === "/take-control/") {
        pageIDForCTA = "take-control";
    } else {
        if (isPageAuth) {
            pageIDForCTA = "auth";
        } else {
            pageIDForCTA = "photos";
        }
    }

    return (
        <>
            {shouldShowBanner ? <Banner /> : <></>}
            <Navbar
                fixed="top"
                expand="lg"
                expanded={expanded}
                className="pt-2 pb-2 pb-md-3 pt-md-3 px-md-5"
                onToggle={toggleHandler}
            >
                <Container className="navbar-container">
                    <Navbar.Brand style={leadingTrailingStyle}>
                        <HomeLink />
                    </Navbar.Brand>
                    <div className="mobile-nav-and-toggle-container">
                        <div className="mobile-nav-login-download">
                            <LoginButtonMobile
                                id={`${pageIDForCTA}-login-button-navbar`}
                                href={loginLink}
                                btnAccentColor={btnAccentColor}
                            >
                                Login
                            </LoginButtonMobile>
                            <DownloadButtonMobile
                                id={`${pageIDForCTA}-download-button-navbar`}
                                onClick={handleClick}
                                btnBackgroundColor={btnBackgroundColor}
                            >
                                Download
                            </DownloadButtonMobile>
                        </div>
                        <Navbar.Toggle
                            aria-controls="navbar-main-nav"
                            className="my-2"
                            onClick={() => setExpanded(!expanded)}
                        >
                            <Bars3Icon width={24} className="nav-toggle" />
                        </Navbar.Toggle>
                    </div>
                    <Navbar.Collapse id="navbar-main-nav" dimension="height">
                        <Nav className="mx-auto gap-2 gap-md-4">
                            <CustomNavLink {...{ currentPath }} to="/#features">
                                Features
                            </CustomNavLink>
                            <CustomNavLink {...{ currentPath }} to="/#pricing">
                                Pricing
                            </CustomNavLink>
                            <CustomNavLink {...{ currentPath }} to="/blog">
                                Blog
                            </CustomNavLink>
                            <CustomNavLink {...{ currentPath }} to="/about">
                                About
                            </CustomNavLink>
                            <CustomNavLink
                                {...{ currentPath }}
                                to="https://help.ente.io"
                            >
                                Help
                            </CustomNavLink>
                        </Nav>
                        <Nav className="desktop-nav-login-download">
                            <div
                                className="pt-4 pb-4 pt-lg-0 pb-lg-0 d-md-block"
                                style={leadingTrailingStyle}
                            >
                                <LoginButtonDesktop
                                    id={`${pageIDForCTA}-login-button-navbar`}
                                    href={loginLink}
                                    btnAccentColor={btnAccentColor}
                                >
                                    Login
                                </LoginButtonDesktop>
                                <DownloadButtonDesktop
                                    id={`${pageIDForCTA}-download-button-navbar`}
                                    onClick={handleClick}
                                    btnBackgroundColor={btnBackgroundColor}
                                >
                                    Download
                                </DownloadButtonDesktop>
                            </div>
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
        </>
    );
};

export default CustomNavbar;

// To make the middle Nav.Links appear visually centered, give both the
// Navbar.Brand and the trailing "Login" Nav.Link the same minimum width.
//
// 100px is an arbitrary width large enough to fit the both the leading and
// trailing content.

const leadingTrailingStyle = {
    // minWidth: "207px",
    margin: "0",
    display: "flex",
    justifyContent: "space-around",
};

const starButtonStyle = (btnBorder: string, btnOutline: string) => ({
    fontFamily:
        "-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif",
    fontWeight: "bold",
    fontSize: "12px",
    width: "120px",
    "--octo-btn-dark-bg": btnBorder,
    "--octo-btn-dark-highlight": "#fff",
    "--octo-btn-dark-label-color": "rgb(0, 0, 0, 0.75)",
    "--octo-btn-dark-color": "rgb(0, 0, 0, 0.75)",
    "--octo-btn-dark-border": btnBorder,
    "--octo-btn-dark-outline": btnOutline,
    "--octo-btn-light-bg": btnBorder,
    "--octo-btn-light-highlight": "#fff",
    "--octo-btn-light-label-color": "rgb(0, 0, 0, 0.75)",
    "--octo-btn-light-color": "rgb(0, 0, 0, 0.75)",
    "--octo-btn-light-border": btnBorder,
    "--octo-btn-light-outline": btnOutline,
});

// This is intentionally not an ExternalLink – we don't want a new tab to open
// when the user tries to login from the header of the website (a new tab
// opening for web.ente.io links from other places is fine, we only need to
// special case the navbar currently).

const LoginButtonDesktop = styled.a.attrs({
    className: "mt-2 mt-md-0",
})<{ btnAccentColor: string }>`
    text-decoration: none;
    border-radius: 12px;
    margin-right: 48px;
    font-weight: 700;
    font-family: Gilroy;
    &:hover {
        color: ${(props) => props.btnAccentColor};
    }
`;

const DownloadButtonDesktop = styled.a.attrs({
    className: "mt-2 mt-md-0",
})<{ btnBackgroundColor: string }>`
    text-decoration: none;
    border-radius: 12px;
    padding: 12px 24px 12px 24px;
    font-weight: 700;
    font-family: Gilroy;
    background: ${(props) => props.btnBackgroundColor};
    color: #ffffff;
    cursor: pointer;
    &:hover {
        color: #ffffff;
    }
`;

const LoginButtonMobile = styled.a<{
    btnAccentColor: string;
}>`
    text-decoration: none;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding-left: 12px;
    padding-right: 12px;
    font-weight: 700;
    font-size: 14px;
    font-family: Gilroy;
    &:hover {
        color: ${(props) => props.btnAccentColor};
    }
`;

const DownloadButtonMobile = styled.a<{
    btnBackgroundColor: string;
}>`
    text-decoration: none;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding-left: 12px;
    padding-right: 12px;
    font-weight: 700;
    font-size: 14px;
    font-family: Gilroy;
    background: ${(props) => props.btnBackgroundColor};
    color: #ffffff;
    cursor: pointer;
    &:hover {
        color: #ffffff;
    }
`;
